import {baseApi} from 'core/redux/store/api';
import {API_VERSION} from 'core/helpers/env';
import {transformErrorResponse} from 'core/redux/store/api/utils';
import {TFilterPagination, TFilterQuery} from 'core/models';
import {IGetArticlesData} from '../models';
import {getListHeadersWithCookies} from 'core/helpers/cookie';
import {buildPaginationQueryString} from 'core/helpers/api';
import {buildHeaders} from 'core/helpers/api/headers';

const ENDPOINT_ARTICLES = `${API_VERSION}/articles`;

type TArticlesListArg = TFilterQuery | void;
type TArticlesByCategoryArg = (TFilterPagination & {category: string}) | void;

const mergePaginatedArticles = (currentCache: IGetArticlesData, newData: IGetArticlesData, pageNumber: number) => {
    if (!newData?.data) {
        return;
    }

    if (!currentCache.data || 1 >= pageNumber) {
        currentCache.data = newData.data;
        return;
    }

    const currentArticles = currentCache.data?.items || [];
    const incomingArticles = newData.data?.items || [];

    currentCache.data = {
        ...currentCache.data,
        ...newData.data,
        items: [...currentArticles, ...incomingArticles],
    };
};

const normalizeFilterKey = (rawFilter: TFilterQuery['filter'] | undefined) => {
    if ('string' === typeof rawFilter) {
        return rawFilter;
    }

    if (Array.isArray(rawFilter)) {
        return rawFilter.join('&');
    }

    return JSON.stringify(rawFilter ?? '');
};

export const blogApiService = baseApi.injectEndpoints({
    overrideExisting: true,
    endpoints: (builder) => ({
        getArticles: builder.query<IGetArticlesData, TArticlesListArg>({
            query: (query) => {
                const filter = query?.filter || '';
                const pageNumber = query?.pageNumber;
                const pageSize = query?.pageSize;
                const headers = getListHeadersWithCookies();

                return {
                    url: `${ENDPOINT_ARTICLES}?${filter || ''}${buildPaginationQueryString(pageNumber, pageSize, '&')}`,
                    headers,
                };
            },
            merge: (currentCache, newData, {arg}) => {
                const pageNumber = arg?.pageNumber ?? 1;
                mergePaginatedArticles(currentCache, newData, pageNumber);
            },
            forceRefetch: ({currentArg, previousArg}) => {
                return currentArg?.filter !== previousArg?.filter || currentArg?.pageNumber !== previousArg?.pageNumber;
            },
            serializeQueryArgs: ({endpointName, queryArgs}) => {
                const normalizedFilter = normalizeFilterKey(queryArgs?.filter as TFilterQuery['filter']);
                return `${endpointName}/${normalizedFilter}`;
            },
            transformErrorResponse,
        }),

        getArticlesByCategory: builder.query<IGetArticlesData, TArticlesByCategoryArg>({
            query: (query) => {
                const category = query?.category || '';
                const pageNumber = query?.pageNumber;
                const pageSize = query?.pageSize;
                const headers = getListHeadersWithCookies();

                return {
                    url: `${ENDPOINT_ARTICLES}/categories/${category}${buildPaginationQueryString(pageNumber, pageSize)}`,
                    headers,
                };
            },
            merge: (currentCache, newData, {arg}) => {
                const pageNumber = arg?.pageNumber ?? 1;
                mergePaginatedArticles(currentCache, newData, pageNumber);
            },
            forceRefetch: ({currentArg, previousArg}) =>
                currentArg?.category !== previousArg?.category || currentArg?.pageNumber !== previousArg?.pageNumber,
            serializeQueryArgs: ({endpointName, queryArgs}) => {
                const category = (queryArgs?.category as string) || '';
                return `${endpointName}/${category}`;
            },
            transformErrorResponse,
        }),

        getArticlesByTag: builder.query<IGetArticlesData, (TFilterPagination & {tag: string}) | void>({
            query: (query) => {
                const tag = query?.tag || '';
                const pageNumber = query?.pageNumber || '';
                const pageSize = query?.pageSize || '';

                const headers = getListHeadersWithCookies();

                return {
                    url: `${ENDPOINT_ARTICLES}/tags/${tag}${buildPaginationQueryString(pageNumber, pageSize)}`,
                    headers,
                };
            },
            merge: (currentCache, newData, {arg}) => {
                const pageNumber = arg?.pageNumber ?? 1;
                mergePaginatedArticles(currentCache, newData, pageNumber);
            },
            forceRefetch: ({currentArg, previousArg}) => {
                return currentArg?.tag !== previousArg?.tag || currentArg?.pageNumber !== previousArg?.pageNumber;
            },
            serializeQueryArgs: ({endpointName, queryArgs}) => {
                const tag = (queryArgs?.tag as string) || '';
                return `${endpointName}/${tag}`;
            },
            transformErrorResponse,
        }),

        getArticleX: builder.query({
            query: (query) => ({
                url: `${ENDPOINT_ARTICLES}/${query.code}`,
                method: 'GET',
                headers: buildHeaders({withSeo: 'detail'}),
            }),
        }),
    }),
});

export const {
    useGetArticlesQuery,
    useGetArticlesByTagQuery,
    useGetArticlesByCategoryQuery,
    util: {getRunningQueriesThunk: getArticlesRunningQueriesThunk},
} = blogApiService;

export const {getArticlesByCategory, getArticlesByTag, getArticleX} = blogApiService.endpoints;
